import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Sparkles, Check, Crown } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { useLanguage } from "@/i18n/LanguageProvider";
import { isPaymentUiBlocked, isNativeIapEnabled } from "@/lib/platform";
import { PREMIUM_BENEFIT_KEYS } from "@/config/pricing";
import IosPaywall from "@/components/paywall/IosPaywall";

const PORTAL_URL = "https://www.aivyv.com/pricing";

/**
 * Discoverable Premium upgrade pitch — visible on ALL platforms (incl. iOS).
 *
 * Apple 3.1.1 split, decided by isPaymentUiBlocked():
 *   - iOS (blocked): pitch + benefits + STATIC text "visit aivyv.com". No link,
 *     no button, no price CTA — purely informational.
 *   - Web / Android: pitch + benefits + "See plans" button that opens the
 *     aivyv.com portal (Capacitor Browser on native Android; in-app /pricing
 *     on web, which is aivyv.com/pricing in production).
 *
 * This screen NEVER renders price + a clickable purchase affordance on iOS.
 */
export default function Upgrade() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const paymentBlocked = isPaymentUiBlocked();
  const iapEnabled = isNativeIapEnabled();
  const [iapOpen, setIapOpen] = useState(false);

  const handleSeePlans = async () => {
    // Reached on web/Android only (the button is not rendered on iOS).
    if (Capacitor.isNativePlatform()) {
      // Android native → open the web billing portal in the system browser.
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: PORTAL_URL });
    } else {
      // Web → in-app pricing (this origin IS aivyv.com in production).
      navigate("/pricing");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div
        className="max-w-2xl mx-auto px-6 pb-16"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px) + 1rem, 2rem)" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t("common.back")}
        </button>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3 mb-8"
        >
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-2xl"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.35)" }}
          >
            <Crown className="w-6 h-6" style={{ color: "hsl(187 72% 60%)" }} />
          </div>
          <h1 className="text-3xl font-display font-medium">{t("home.upgradeCard.title")}</h1>
          <p className="text-muted-foreground">{t("home.upgradeCard.subtitle")}</p>
        </motion.div>

        {/* What you unlock */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="vyv-card p-5 mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={15} style={{ color: "hsl(187 72% 55%)" }} />
            <p className="font-display text-[13px] font-semibold text-foreground/85">
              {t("upgrade.unlocksTitle")}
            </p>
          </div>
          <ul className="space-y-3">
            {PREMIUM_BENEFIT_KEYS.map((key) => (
              <li key={key} className="flex items-start gap-3">
                <Check size={16} className="mt-0.5 shrink-0" style={{ color: "hsl(187 72% 55%)" }} />
                <span className="font-body text-sm text-foreground/80">{t(key)}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Conversion / info — the ONLY platform-conditional block. */}
        {iapEnabled ? (
          // iOS native IAP path (Apple 3.1.1-compliant): open the StoreKit paywall.
          // "See plans", NOT the purchase CTA: this screen promises no charge —
          // the paywall sheet is where the exact commitment is spelled out.
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => setIapOpen(true)}
            className="w-full rounded-xl py-3.5 font-display text-[14px] font-semibold transition-transform active:scale-[0.98]"
            style={{ background: "hsl(187 72% 45%)", color: "#04121a" }}
          >
            {t("upgrade.seePlansIap")}
          </motion.button>
        ) : paymentBlocked ? (
          // iOS with IAP flag off (Apple 3.1.1): static text only. No link/button/price.
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-center font-body text-sm text-foreground/55 leading-relaxed px-2"
          >
            {t("paywall.ios.webHint")}
          </motion.p>
        ) : (
          // Web / Android: clickable CTA to the billing portal.
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={handleSeePlans}
            className="w-full rounded-xl py-3.5 font-display text-[14px] font-semibold transition-transform active:scale-[0.98]"
            style={{ background: "hsl(187 72% 45%)", color: "#04121a" }}
          >
            {t("upgrade.seePlans")}
          </motion.button>
        )}
      </div>

      <IosPaywall open={iapOpen} onClose={() => setIapOpen(false)} />
    </div>
  );
}

